<template>
    <section id="technology" class="tech">
        <div class="ob-dots tech-dots" />
        <div class="tech-grid">
            <div class="tech-left">
                <div class="tech-eyebrow">The technology</div>
                <h2 class="tech-h2">Four ways<br>to reach you.</h2>
                <p class="tech-intro">Not every street is the same, so we do not force one technology on everybody. We look at your address and connect you on whichever gets you the best result — and the promise we make you is the same either way.</p>
                <div class="tech-callout">
                    <div class="tech-callout-k">What this means for you</div>
                    <p class="tech-callout-p">You do not need to choose a technology. Tell us where you are, and we will tell you what you can have.</p>
                </div>
                <a :href="COVERAGE_URL" target="_blank" rel="noopener" class="tech-map-link ob-press">Check the coverage map ↗</a>
            </div>
            <div>
                <div v-for="(b, i) in BEARERS" :key="b.n" class="bearer">
                    <button
                        class="bearer-row ob-tap"
                        :aria-expanded="store.bearer === i"
                        :aria-controls="`ob-bearer-panel-${i}`"
                        @click="store.toggleBearer(i)"
                    >
                        <span class="bearer-grid">
                            <span class="bearer-n">{{ b.n }}</span>
                            <span class="bearer-main">
                                <span class="bearer-name">{{ b.name }}</span>
                                <span class="bearer-role">{{ b.role }}</span>
                            </span>
                            <span class="bearer-cap">{{ b.cap }}</span>
                            <span class="bearer-chev" :class="{ open: store.bearer === i }">⌄</span>
                        </span>
                    </button>
                    <div v-if="store.bearer === i" :id="`ob-bearer-panel-${i}`" role="region" class="bearer-panel">
                        <div class="bearer-cols">
                            <div>
                                <div class="bearer-col-k">Typical use</div>
                                <div class="bearer-col-v">{{ b.use }}</div>
                            </div>
                            <div>
                                <div class="bearer-col-k">Good to know</div>
                                <div class="bearer-col-v">{{ b.note }}</div>
                            </div>
                        </div>
                        <a href="#contact" class="bearer-cta ob-press">Ask if this reaches me →</a>
                    </div>
                </div>
                <div class="bearer-endcap" />

                <div class="tech-partner">Satellite is delivered with our chosen partner, <span class="tech-partner-name">WorldLink</span>.</div>

                <div class="promise ob-pinkable">
                    <div class="promise-eyebrow">The promise behind all four</div>
                    <h3 class="promise-h3">However we reach you, the commitment is the same.</h3>
                    <div class="promise-grid">
                        <div>
                            <div class="promise-v">99.9%</div>
                            <div class="promise-k">The uptime we design and operate to.</div>
                        </div>
                        <div>
                            <div class="promise-v">24/7</div>
                            <div class="promise-k">A Freetown team on the line, not a queue.</div>
                        </div>
                        <div>
                            <div class="promise-v">First</div>
                            <div class="promise-k">You hear about an outage from us before you call.</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script lang="ts" setup>
    import { BEARERS, COVERAGE_URL } from '@/data/content'
    import { useLandingStore } from '@/stores/landing'

    const store = useLandingStore()
</script>

<style scoped>
    .tech { position: relative; max-width: 1240px; margin: 0 auto; padding: 88px 44px; }
    .tech-dots { position: absolute; left: 44px; bottom: 60px; width: 200px; height: 150px; opacity: .4; pointer-events: none; }
    .tech-grid { display: grid; grid-template-columns: .9fr 1.1fr; gap: 60px; align-items: start; }
    .tech-left { position: sticky; top: 110px; }
    .tech-eyebrow { font-size: 12px; font-weight: 600; letter-spacing: .16em; text-transform: uppercase; color: var(--ob-blue-deep); }
    .tech-h2 {
        margin: 16px 0 0;
        font-size: clamp(34px, 4.2vw, 58px);
        font-weight: 800;
        letter-spacing: -.028em;
        line-height: 1;
        color: var(--ob-ink);
    }
    .tech-intro { font-size: 17px; line-height: 1.6; color: var(--ob-ink-soft); margin: 24px 0 0; max-width: 400px; }
    .tech-callout {
        margin-top: 30px;
        padding: 22px 24px;
        border-left: 3px solid var(--ob-blue-deep);
        background: var(--ob-w-a72);
        border-radius: 0 14px 14px 0;
    }
    .tech-callout-k { font-size: 12px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--ob-blue-deep); }
    .tech-callout-p { margin: 10px 0 0; font-size: 15.5px; line-height: 1.55; color: var(--ob-ink-deep); }
    .tech-map-link {
        display: inline-flex;
        align-items: center;
        gap: 9px;
        margin-top: 24px;
        font-size: 14.5px;
        font-weight: 700;
        color: var(--ob-blue-deep);
        text-decoration: none;
    }

    .bearer { border-top: 1px solid var(--ob-navy-a14); }
    .bearer-endcap { border-top: 1px solid var(--ob-navy-a14); }
    .bearer-row {
        display: block;
        width: 100%;
        cursor: pointer;
        border: none;
        background: transparent;
        padding: 26px 16px 26px 0;
        font-family: 'Poppins', sans-serif;
    }
    .bearer-grid { display: grid; grid-template-columns: auto 1fr auto auto; gap: 24px; align-items: baseline; width: 100%; }
    .bearer-n { font-size: 12px; font-weight: 700; letter-spacing: .1em; color: var(--ob-ink-code); font-variant-numeric: tabular-nums; }
    .bearer-main { text-align: left; }
    .bearer-name { display: block; font-size: clamp(22px, 2.4vw, 30px); font-weight: 700; letter-spacing: -.018em; line-height: 1.1; color: var(--ob-ink); }
    .bearer-role { display: block; margin-top: 10px; font-size: 15px; line-height: 1.55; color: var(--ob-ink-sub); max-width: 440px; }
    .bearer-cap {
        font-size: 11.5px;
        font-weight: 600;
        letter-spacing: .08em;
        text-transform: uppercase;
        color: var(--ob-blue-deep);
        white-space: nowrap;
        padding: 6px 12px;
        border: 1px solid var(--ob-blue-a30);
        border-radius: 999px;
    }
    .bearer-chev { flex: none; font-size: 19px; color: var(--ob-blue-deep); transition: transform .25s; }
    .bearer-chev.open { transform: rotate(180deg); }
    .bearer-panel {
        margin: 0 16px 26px 0;
        padding: 18px 20px;
        border-radius: 14px;
        background: var(--ob-w-a78);
        border: 1px solid var(--ob-navy-a10);
    }
    .bearer-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
    .bearer-col-k { font-size: 11px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: var(--ob-blue-deep); }
    .bearer-col-v { margin-top: 7px; font-size: 14.5px; line-height: 1.55; color: var(--ob-ink); }
    .bearer-cta {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        margin-top: 18px;
        padding: 11px 20px;
        border-radius: 999px;
        background: var(--ob-blue);
        color: var(--ob-white);
        font-size: 13.5px;
        font-weight: 700;
        white-space: nowrap;
        text-decoration: none;
    }

    .tech-partner { margin-top: 18px; font-size: 14px; line-height: 1.6; color: var(--ob-ink-sub); }
    .tech-partner-name { font-weight: 600; color: var(--ob-ink); }

    .promise {
        margin-top: 38px;
        padding: 38px 40px;
        border-radius: 24px;
        color: var(--ob-white);
        background: var(--ob-grad-full);
        box-shadow: var(--ob-shadow-glow-lg);
    }
    .promise-eyebrow { font-size: 12px; font-weight: 600; letter-spacing: .16em; text-transform: uppercase; color: var(--ob-w-a86); }
    .promise-h3 {
        margin: 14px 0 0;
        font-size: clamp(22px, 2.4vw, 30px);
        font-weight: 700;
        letter-spacing: -.02em;
        line-height: 1.15;
        color: var(--ob-white);
    }
    .promise-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 26px; margin-top: 30px; }
    .promise-v { font-size: clamp(26px, 3vw, 38px); font-weight: 800; letter-spacing: -.03em; line-height: 1; }
    .promise-k { margin-top: 8px; font-size: 14px; line-height: 1.5; color: var(--ob-w-a90); }

    @media (max-width: 1024px) {
        .tech-grid { grid-template-columns: 1fr; gap: 40px; }
        .tech-left { position: static; }
    }
    @media (max-width: 700px) {
        .tech { padding: 56px 20px; }
        .tech-dots { display: none; }
        .promise { padding: 26px 24px; }
        .promise-grid { gap: 18px; }
    }
    @media (max-width: 640px) {
        .bearer-grid {
            grid-template-columns: auto 1fr auto;
            grid-template-areas:
                "n main chev"
                ". cap .";
            gap: 14px 16px;
        }
        .bearer-n { grid-area: n; }
        .bearer-main { grid-area: main; }
        .bearer-chev { grid-area: chev; }
        .bearer-cap { grid-area: cap; justify-self: start; }
        .bearer-cols { grid-template-columns: 1fr; }
        .bearer-panel { margin-right: 0; }
        .promise-grid { grid-template-columns: 1fr; }
    }
</style>
